import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSlug } from 'utils/slug.jsx';
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useBackButton from "hooks/useBackButton";
import validate from "utils/validate";
import { BackButtonPage } from "components/__new/common/BackButton";
import PageContent from "components/__new/common/PageContent";
import Maybe from "components/__new/common/Maybe";
import { ButtonLoader } from "components/__new/common/form";
import CreateEditForm from "components/__new/employees/CreateEditForm";
import * as employeesActions from "store/entities/employees/action";
import * as banksActions from "store/ui/banks/action";
import * as groupsActions from "store/entities/groups/action";
import * as payrollSetupActions from "store/entities/payroll/payrollSetup/action";
import useBanks from 'hooks/useBanks';
import useLoginInfo from 'hooks/useLoginInfo';



const Form = () => {
    const { id, slug } = useParams();
    const {userInfo} = useLoginInfo();

    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();
    const {goBack} = useBackButton();
    const { employeeLoading, employee, employeeUpdateLoading, employeeUpdateSuccess } = useSelector((s) => s.entities.employees);
    const { banks, banksLoading } = useBanks(userInfo?.token);
    const { getAllGroupsLoading: groupsLoading, allGroups: groups } = useSelector((s) => s.entities.groups);
    const { pensionProvidersLoading, pensionProvidersData } = useSelector((s) => s.entities.payrollSetup);


    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        jobTitle: "",
        staffId: "",
        groupPolicyId: "",
        netMonthlySalary: "",
        grossAnnualPay: "",
        bankName: "",
        bankCode: "",
        bankAccountNumber: "",
        taxIdentificationNumber: "",
        pensionAdministrator: "",
        pensionNumber: "",
        pensionAdministratorCode: "",
    })


    const getBankName = () => {
        const bankData = banks;
        for (var i = 0; i < bankData?.length; i++){
            if (bankData[i].code === form.bankCode){
                return (bankData[i].name);
            }
        }
        return null;
    }


    const getPfaCode = () => {
        const selectedAdministrator = pensionProvidersData?.data?.find((item) => item?.name === form?.pensionAdministrator);
        if(selectedAdministrator) {
            return selectedAdministrator?.pfa_code;
        }
    }

    const submitForm = async e => {
        e.preventDefault();
        const validateForm = {...form};
        delete validateForm["pensionAdministrator"];
        delete validateForm["pensionNumber"];
        delete validateForm["taxIdentificationNumber"];
        delete validateForm["pensionAdministratorCode"];
        delete validateForm["staffId"];
        delete validateForm["grossAnnualPay"];
        const errors = validate(validateForm);
        if (errors){
            for (var x in errors) {
                toast.error(errors[x]);
            };
            return;
        }
        else{
            const bankName = getBankName();
            const pensionAdministratorCode = getPfaCode();
            const params = {...form, bankName, pensionAdministratorCode};
            setForm(params);
            dispatch(employeesActions.editEmployee(id, params));
        }
    }


    useEffect(() => {
        if (id){
            dispatch(employeesActions.getEmployee(id));
            dispatch(banksActions.getAllBanks());
            dispatch(groupsActions.getAllGroups());
            dispatch(payrollSetupActions.getPensionProviders());
        }
        // eslint-disable-next-line
    }, [id, location])


    useEffect(() => {
        if (employee?.status === "success"){
            const actualSlug = getSlug(employee?.employee?.firstName || employee?.employee?.fullName);
            if (slug !== actualSlug){
                history.replace(`/employees/edit/${id}/${actualSlug}`);
            }

            let newEmployeeForm = {
                firstName: employee?.employee?.firstName ?? "",
                lastName: employee?.employee?.lastName ?? "",
                email: employee?.employee?.email ?? "",
                phoneNumber: employee?.employee?.phoneNumber ?? "",
                jobTitle: employee?.employee?.jobTitle ?? "",
                staffId: employee?.employee?.staffId ?? "",
                groupPolicyId: employee?.employee?.groupPolicyId !== null ? employee?.employee?.groupPolicyId : "general-group",
                netMonthlySalary: employee?.employee?.netMonthlySalary ?? "",
                grossAnnualPay: employee?.employee?.grossAnnualPay ?? "",
                bankName: employee?.employee?.bankName ?? "",
                bankCode: employee?.employee?.bankCode ?? "",
                bankAccountNumber: employee?.employee?.bankAccountNumber ?? "",
                taxIdentificationNumber: employee?.employee?.taxIdentificationNumber ?? "",
                pensionAdministrator: employee?.employee?.pensionAdministrator ?? "",
                pensionNumber: employee?.employee?.pensionNumber ?? "",
            }
            setForm(newEmployeeForm);
        }
        // eslint-disable-next-line
    }, [employee])


    useEffect(() => {
        if (employeeUpdateSuccess === true){
            dispatch(employeesActions.resetEmployeeEditSuccess());
            goBack();
        }
        // eslint-disable-next-line
    }, [employeeUpdateSuccess]);


    return ( 
        <div>


            <DynamicHeadTag
                headerText="Edit Employees"
                headerDescription="Manage and monitor your employees' payment activity."
            />
            

            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex">
                        <BackButtonPage />
                        Edit Employee
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <PageContent
                loading={employeeLoading}
                data={employee}
                loadingText="Loading..."
                emptyText="Employee not found"
                errorText="Unable to fetch employee information"
            />


            <Maybe condition={employeeLoading === false && employee?.status === "success"}>
                <div className="mt-6 box">
                    <div className="max-w-2xl">
                        <form onSubmit={submitForm}>

                            <div className="mb-6">
                                Make changes to employee details from here
                            </div>

                            <CreateEditForm
                                form={form}
                                setForm={setForm}
                                groupsLoading={groupsLoading}
                                groups={groups}
                                banksLoading={banksLoading}
                                banks={banks}
                                pensionProvidersLoading={pensionProvidersLoading}
                                pensionProvidersData={pensionProvidersData}
                            />

                            <div className="mt-4 flex space-x-4">
                                <ButtonLoader type="submit" loading={employeeUpdateLoading} className="btn btn-ep-primary">
                                    Edit Employee
                                </ButtonLoader>
                                <button type="button" onClick={goBack} className="btn btn-transparent-primary">
                                    Cancel
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </Maybe>

            
        </div>
    )
}


export default Form;